import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getFirestore, collection, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { app } from '../firebase';
import { functions } from '../firebase';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';

const db = getFirestore(app);

export default function DomainsList() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const coll = collection(db, 'domains');
        const unsub = onSnapshot(coll, (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setDomains(list);
            setLoading(false);
        }, (err) => {
            console.error('domains snapshot', err);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete domain? This will not cascade delete rooms and participants automatically.')) return;
        setError('');
        setSuccess('');
        setDeleting(id);
        try {
            const batch = writeBatch(db);
            batch.delete(doc(db, 'domains', id));
            await batch.commit();
        } catch (e) {
            console.error('delete domain', e);
            alert('Failed to delete domain');
        } finally {
            setDeleting(null);
        }
    };

    const handleResetDeviceState = async (domain) => {
        if (!window.confirm(`Clear transient device state for domain ${domain.name}? This keeps bonding and configuration but removes stale presence, signaling, and call session data.`)) return;
        setError('');
        setSuccess('');
        setResetting(domain.id);
        try {
            const callable = httpsCallable(functions, 'resetDomainDeviceState');
            const result = await callable({ domainId: domain.id });
            const data = result?.data || {};
            const participantCount = typeof data.participantCount === 'number' ? data.participantCount : 0;
            const deviceCount = typeof data.deviceCount === 'number' ? data.deviceCount : 0;
            const deletedCallSessions = typeof data.deletedCallSessions === 'number' ? data.deletedCallSessions : 0;
            setSuccess(`Cleared transient state for ${domain.name}: ${participantCount} participant records, ${deviceCount} devices, ${deletedCallSessions} call sessions.`);
        } catch (e) {
            console.error('resetDomainDeviceState', e);
            const message = e?.details || e?.message || 'Failed to clear device state for the selected domain';
            setError(typeof message === 'string' ? message : 'Failed to clear device state for the selected domain');
        } finally {
            setResetting(null);
        }
    };

    if (loading) return <Box sx={{ p: 2 }}>Loading domains...</Box>;

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Domains</Typography>
                <Button variant="contained" onClick={() => navigate('/domains/new')}>Create Domain</Button>
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {domains.length === 0 ? (
                <Typography>No domains found.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {domains.map(r => (
                                <TableRow key={r.id} hover>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>
                                        <Button component={Link} to={`/domains/${r.id}/edit`} size="small">Edit</Button>
                                        <Button onClick={() => handleResetDeviceState(r)} size="small" color="warning" disabled={resetting === r.id || deleting === r.id}>{resetting === r.id ? 'Clearing...' : 'Clear Device State'}</Button>
                                        <Button onClick={() => handleDelete(r.id)} size="small" color="error" disabled={deleting === r.id}>{deleting === r.id ? 'Deleting...' : 'Delete'}</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
